import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  GridView as GridViewIcon,
  Restaurant as RestaurantIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Product } from "../types";
import { categoryBg, petpooja } from "../posPetpoojaTheme";

interface ProductsPanelProps {
  products: Product[];
  onAddToCart: (product: Product) => void;
  loading?: boolean;
  disabled?: boolean;
}

const ProductsPanel: React.FC<ProductsPanelProps> = ({
  products,
  onAddToCart,
  loading = false,
  disabled = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchProduct, setSearchProduct] = useState("");

  const categories = useMemo(() => {
    const cats = Array.from(new Set(products.map((p) => p.category)));
    return ["All", ...cats];
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchProduct.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchProduct, selectedCategory]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        bgcolor: petpooja.bg,
      }}
    >
      {/* Category rail — Petpooja vertical menu */}
      <Box
        sx={{
          width: 96,
          flexShrink: 0,
          bgcolor: petpooja.panel,
          borderRight: `1px solid ${petpooja.border}`,
          overflowY: "auto",
          py: 0.5,
        }}
      >
        {categories.map((cat, idx) => {
          const active = selectedCategory === cat;
          const count =
            cat === "All"
              ? products.length
              : products.filter((p) => p.category === cat).length;
          return (
            <Box
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              sx={{
                mx: 0.75,
                my: 0.5,
                py: 1,
                px: 0.5,
                borderRadius: 1.5,
                cursor: "pointer",
                textAlign: "center",
                bgcolor: active ? petpooja.accent : categoryBg(idx),
                color: active ? "#fff" : petpooja.text,
                border: active
                  ? `2px solid ${petpooja.accentDark}`
                  : "2px solid transparent",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: petpooja.accent,
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: active ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 0.5,
                }}
              >
                {cat === "All" ? (
                  <GridViewIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                ) : (
                  <RestaurantIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.65rem",
                  lineHeight: 1.2,
                  display: "block",
                }}
              >
                {cat.length > 10 ? `${cat.slice(0, 9)}…` : cat}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6rem",
                  opacity: active ? 0.9 : 0.65,
                }}
              >
                {count}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Menu grid */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: petpooja.panel,
            borderBottom: `1px solid ${petpooja.border}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} color={petpooja.text}>
            {selectedCategory === "All" ? "All Menu Items" : selectedCategory}
          </Typography>
          <ChipCount count={filteredProducts.length} />
          <TextField
            size="small"
            placeholder="Search item / short code..."
            value={searchProduct}
            onChange={(e) => setSearchProduct(e.target.value)}
            disabled={disabled}
            sx={{
              ml: "auto",
              width: { xs: 180, md: 260 },
              "& .MuiOutlinedInput-root": {
                bgcolor: petpooja.bg,
                fontSize: "0.875rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: petpooja.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {disabled && (
          <Box
            sx={{
              px: 2,
              py: 0.75,
              bgcolor: petpooja.accentLight,
              borderBottom: `1px solid ${petpooja.border}`,
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" fontWeight={600} color={petpooja.accentDark}>
              Select a table to start billing
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1.5 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: petpooja.accent }} />
            </Box>
          )}

          {!loading && filteredProducts.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <RestaurantIcon sx={{ fontSize: 48, color: petpooja.border, mb: 1 }} />
              <Typography variant="body2" color={petpooja.textMuted}>
                No menu items found
              </Typography>
            </Box>
          )}

          {!loading && filteredProducts.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(5, 1fr)",
                },
                gap: 1,
              }}
            >
              {filteredProducts.map((product, idx) => (
                <Box
                  key={product.id}
                  onClick={() => !disabled && onAddToCart(product)}
                  sx={{
                    bgcolor: petpooja.panel,
                    border: `1px solid ${petpooja.border}`,
                    borderRadius: 1.5,
                    p: 1.25,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.55 : 1,
                    transition: "all 0.15s",
                    borderTop: `3px solid ${categoryBg(idx)}`,
                    "&:hover": disabled
                      ? {}
                      : {
                          borderColor: petpooja.accent,
                          boxShadow: "0 4px 16px rgba(232,93,4,0.15)",
                          transform: "translateY(-1px)",
                        },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: petpooja.accent,
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      textTransform: "uppercase",
                      display: "block",
                      mb: 0.25,
                    }}
                  >
                    {product.category}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={petpooja.text}
                    sx={{
                      lineHeight: 1.25,
                      minHeight: 36,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {product.name}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    sx={{ color: petpooja.greenDark, mt: 0.75 }}
                  >
                    ₹{product.salePrice.toFixed(0)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ChipCount: React.FC<{ count: number }> = ({ count }) => (
  <Box
    sx={{
      px: 1,
      py: 0.25,
      borderRadius: 1,
      bgcolor: petpooja.accentLight,
      color: petpooja.accentDark,
      fontSize: "0.75rem",
      fontWeight: 700,
    }}
  >
    {count} items
  </Box>
);

export default ProductsPanel;
